import { ChangeEvent, DragEvent, useRef, useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ArrowDown2, ArrowRight2, DocumentUpload } from "iconsax-reactjs";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  updateKybRequirementDocument,
  updateKybRequirementValue,
  uploadKybDocumentFile,
} from "../../../shared/api/kyb";
import { getApiErrorMessage } from "../../../shared/lib";
import { Button, HeadingText } from "../../../shared/ui";
import KybComplianceUpdateLayout from "../components/KybComplianceUpdateLayout";

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/jpg",
  "application/pdf",
];

const normalizeRequirementKey = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const identificationTypeOptions = [
  { label: "BVN", value: "BVN" },
  { label: "Passport", value: "Passport" },
  { label: "National ID", value: "National ID" },
  { label: "Driver's license", value: "Driver's license" },
];

const KybComplianceUpdateInformationPage = () => {
  const { businessId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [identificationType, setIdentificationType] = useState("BVN");
  const [identificationNumber, setIdentificationNumber] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const documentName =
    searchParams.get("documentName") || "Certificate of incorporation";
  const informationRequirementKey = normalizeRequirementKey(
    searchParams.get("infoKey") || "identification_number",
  );
  const documentRequirementKey = normalizeRequirementKey(
    searchParams.get("documentKey") || documentName,
  );
  const canSubmit =
    Boolean(businessId) &&
    Boolean(identificationNumber.trim()) &&
    Boolean(selectedFile) &&
    !isSubmitting;

  const selectFile = (file?: File | null) => {
    if (!file) return;

    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      setSelectedFile(null);
      setFileError("Upload a JPEG, PNG, JPG, or PDF file.");
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setSelectedFile(null);
      setFileError("File size must be 5 MB or less.");
      return;
    }

    setSelectedFile(file);
    setFileError("");
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    selectFile(event.target.files?.[0]);
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    selectFile(event.dataTransfer.files?.[0]);
  };

  const handleSubmit = async () => {
    if (!canSubmit || !selectedFile) return;

    setIsSubmitting(true);

    try {
      const uploadedDocument = await uploadKybDocumentFile(selectedFile);
      if (!uploadedDocument.url) {
        throw new Error("Unable to get uploaded document URL.");
      }

      await Promise.all([
        updateKybRequirementValue({
          businessId,
          requirementKey: informationRequirementKey,
          textValue: `${identificationType}: ${identificationNumber.trim()}`,
        }),
        updateKybRequirementDocument({
          businessId,
          requirementKey: documentRequirementKey,
          document: uploadedDocument,
        }),
      ]);

      toast.success("Information submitted successfully.");
      setIdentificationNumber("");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "Unable to submit updated information."),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <KybComplianceUpdateLayout title="Update information">
      <section className="w-full max-w-[650px]">
        <HeadingText className="aku-heading-h2-medium text-[#161827]">
          Update your information
        </HeadingText>

        <div className="mt-9">
          <div
            id="kyb-identification-number-label"
            className="aku-body-small-medium text-[#2C2F44]"
          >
            Identification Number
          </div>
          <div className="mt-2 flex min-h-14 items-center rounded-[10px] bg-[#E9ECF8] px-4 focus-within:ring-2 focus-within:ring-[#D9DEFF]">
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  disabled={isSubmitting}
                  className="inline-flex h-10 shrink-0 items-center gap-2 rounded-[8px] px-2 aku-body-small-medium text-[#383B56] outline-none hover:bg-[#EEF1FB] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {identificationType}
                  <ArrowDown2 size={16} className="text-[#6F739B]" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="start"
                  sideOffset={8}
                  className="z-[120] min-w-[180px] rounded-[14px] border border-[#E1E6FF] bg-white p-2 shadow-[0_16px_36px_rgba(15,20,40,0.12)]"
                >
                  {identificationTypeOptions.map((option) => (
                    <DropdownMenu.Item
                      key={option.value}
                      onSelect={() => setIdentificationType(option.value)}
                      className={`cursor-pointer rounded-[10px] px-3 py-2 aku-body-small-medium outline-none transition-colors focus:bg-[#F5F6FD] ${
                        option.value === identificationType
                          ? "bg-[#F1ECFF] text-[#5E01D6]"
                          : "text-[#383B56]"
                      }`}
                    >
                      {option.label}
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
            <input
              aria-labelledby="kyb-identification-number-label"
              value={identificationNumber}
              disabled={isSubmitting}
              onChange={(event) => setIdentificationNumber(event.target.value)}
              placeholder="Enter 11-digit bank verification number"
              className="h-full min-w-0 flex-1 bg-transparent aku-body-small-regular text-[#161827] placeholder:text-[#6F739B] outline-none disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>
        </div>

        <div className="mt-8">
          <div className="flex items-center justify-between gap-4">
            <div className="aku-body-small-medium text-[#4A4F73]">
              {documentName}
            </div>
            <button
              type="button"
              className="inline-flex items-center gap-2 aku-body-small-medium text-[#6F00FF] hover:opacity-80"
            >
              See sample <ArrowRight2 size={18} color="#6F00FF" />
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".jpeg,.jpg,.png,.pdf,image/jpeg,image/png,application/pdf"
            className="hidden"
            disabled={isSubmitting}
            onChange={handleFileChange}
          />

          <button
            type="button"
            disabled={isSubmitting}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(event) => event.preventDefault()}
            onDrop={handleDrop}
            className="mt-3 flex w-full items-center gap-4 rounded-[10px] border border-dashed border-[#DDE2FA] bg-white px-5 py-5 text-left transition-colors hover:bg-[#FBFCFF] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#E8DDFF]">
              <DocumentUpload size={22} color="#6F00FF" variant="Bold" />
            </span>
            <span className="min-w-0">
              <span className="block truncate aku-body-small-medium text-[#161827]">
                {selectedFile?.name || "Upload document"}
              </span>
              <span className="mt-1 block aku-body-small-regular text-[#8A90B5]">
                JPEG, PNG, JPG, PDF • 5 MB max.
              </span>
            </span>
          </button>

          {fileError ? (
            <p className="mt-2 aku-body-small-regular text-[#C24E4E]">
              {fileError}
            </p>
          ) : null}
        </div>

        <div className="mt-10 flex justify-end">
          <Button
            type="button"
            disabled={!canSubmit}
            onClick={handleSubmit}
            className="h-12 rounded-[10px] px-8 aku-body-normal-medium disabled:bg-[#D8C9FF]"
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </div>
      </section>
    </KybComplianceUpdateLayout>
  );
};

export default KybComplianceUpdateInformationPage;
